import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ButtonComponent } from "shared/components";
import { modalVariants, childVariants } from "shared/utils";
import "../styles/EditCertificatesModal.css";

import { ORIGINAL_DEFAULT_THEMES } from "../../../components/Themes/DefaultThemes.jsx";

const EditDefaultThemesModal = ({ isOpen, onClose, currentThemes, onSave }) => {
  const [themes, setThemes] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [editForm, setEditForm] = useState({
    name: "",
    colors: {
      textColor: "",
      backgroundColor: "",
      headerColor: "",
    },
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      if (currentThemes && Array.isArray(currentThemes)) {
        setThemes([...currentThemes]);
      } else {
        setThemes([]);
      }
      setEditingIndex(null);
      setIsAddingNew(false);
      setEditForm({
        name: "",
        colors: {
          textColor: "",
          backgroundColor: "",
          headerColor: "",
        },
      });
      setErrors({});
    }
  }, [isOpen, currentThemes]);

  const handleEditClick = (index) => {
    setEditingIndex(index);
    setIsAddingNew(false);
    setEditForm({ ...themes[index] });
  };

  const handleAddNewClick = () => {
    setIsAddingNew(true);
    setEditingIndex(null);
    setEditForm({
      name: "",
      colors: {
        textColor: "",
        backgroundColor: "",
        headerColor: "",
      },
    });
    setErrors({});
  };

  const handleSaveEdit = () => {
    const newErrors = {};

    if (!editForm.name.trim()) {
      newErrors.name = "Theme name is required";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    let newThemes;
    if (isAddingNew) {
      newThemes = [...themes, { ...editForm }];
    } else {
      newThemes = [...themes];
      newThemes[editingIndex] = { ...editForm };
    }

    // Save to draft immediately
    onSave(newThemes);

    setThemes(newThemes);
    setEditingIndex(null);
    setIsAddingNew(false);
    setErrors({});
  };

  const handleDeleteTheme = (index) => {
    const newThemes = themes.filter((_, i) => i !== index);
    onSave(newThemes);
    setThemes(newThemes);
    setEditingIndex(null);
  };

  const handleColorChange = (field, value) => {
    setEditForm((prev) => ({
      ...prev,
      colors: {
        ...prev.colors,
        [field]: value,
      },
    }));
  };

  const colorFields = [
    { key: "textColor", label: "Text Color", icon: "bx bxs-text" },
    { key: "backgroundColor", label: "Background Color", icon: "bx bxs-paint" },
    { key: "headerColor", label: "Header Color", icon: "bx bxs-object-group" },
  ];

  if (!isOpen) return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="preview-modal active-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            className="preview-modal-content"
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: "1000px",
              maxHeight: "90vh",
              overflow: "auto",
              width: "95%",
            }}
          >
            {/* Close Button */}
            <motion.i
              className="uil uil-times preview-modal-close"
              onClick={onClose}
              whileHover={{ rotate: 90, scale: 1.2 }}
              whileTap={{ scale: 0.9 }}
              style={{ cursor: "pointer" }}
            ></motion.i>

            {/* Title */}
            <motion.h3
              className="preview-modal-title"
              variants={childVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              🎨 Edit Default Themes
            </motion.h3>

            {/* Main Container - Two Column Layout */}
            <motion.div
              style={{
                display: "flex",
                gap: "0.5rem",
                height: "440px",
              }}
            >
              {/* Left Side - Grid (Stays at flex 1, cards naturally wrap to next row) */}
              <motion.div
                layout
                style={{
                  flex: 1,
                  overflow: "visible",
                  paddingRight: "0.5rem",
                }}
              >
                <h4
                  style={{
                    fontSize: "1rem",
                    fontWeight: "700",
                    marginBottom: "0.75rem",
                    color: "var(--title-color)",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                  }}
                >
                  <span style={{ fontSize: "1.2rem" }}>📋</span> Existing Themes
                  ({themes.length})
                </h4>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    marginBottom: "1rem",
                  }}
                >
                  <ButtonComponent
                    buttonText="Restore defaults"
                    buttonTitle="Restore the original default theme set"
                    onClick={() => {
                      setThemes([...ORIGINAL_DEFAULT_THEMES]);
                      onSave([...ORIGINAL_DEFAULT_THEMES]);
                      setEditingIndex(null);
                      setIsAddingNew(false);
                      setErrors({});
                    }}
                    className="btn-secondary"
                  />
                </div>

                {themes.length > 0 ||
                (editingIndex === null && !isAddingNew) ? (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(175px, 1fr))",
                      gap: "0.75rem",
                    }}
                  >
                    {themes.map((theme, index) => (
                      <motion.div
                        key={index}
                        layout
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{
                          type: "spring",
                          stiffness: 150,
                          damping: 25,
                        }}
                        className="glass__card theme__card"
                        style={{
                          border: "2px solid var(--title-color)",
                          borderRadius: "0.5rem",
                          padding: "0.65rem",
                          position: "relative",
                          backgroundColor:
                            "color-mix(in srgb, var(--title-color) 5%, var(--body-color))",
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                        }}
                      >
                        {/* Edit and Delete Icons */}
                        <div
                          style={{
                            position: "absolute",
                            top: "0.35rem",
                            right: "0.35rem",
                            display: "flex",
                            gap: "0.25rem",
                          }}
                        >
                          <motion.button
                            whileHover={{ scale: 1.15 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditClick(index);
                            }}
                            className="glass__button"
                            style={{
                              border: "none",
                              borderRadius: "50%",
                              width: "2rem",
                              height: "2rem",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              cursor: "pointer",
                              color: "var(--body-color)",
                              background: "var(--title-color)",
                              padding: 0,
                            }}
                            title="Edit theme"
                          >
                            <i
                              className="bx bx-pencil"
                              style={{ fontSize: "14px" }}
                            ></i>
                          </motion.button>
                          <motion.button
                            whileHover={{ scale: 1.15 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteTheme(index);
                            }}
                            className="glass__button"
                            style={{
                              border: "none",
                              borderRadius: "50%",
                              width: "2rem",
                              height: "2rem",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              cursor: "pointer",
                              color: "var(--body-color)",
                              background: "var(--title-color)",
                              padding: 0,
                            }}
                            title="Delete theme"
                          >
                            <i
                              className="bx bx-trash"
                              style={{ fontSize: "14px" }}
                            ></i>
                          </motion.button>
                        </div>

                        {/* Theme Content */}
                        <div style={{ paddingRight: "2.5rem" }}>
                          {/* Theme Name */}
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "0.5rem",
                              marginBottom: "0.6rem",
                            }}
                          >
                            <span
                              style={{
                                fontSize: "0.75rem",
                                fontWeight: "600",
                                color: "var(--title-color)",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {theme.name || "Untitled"}
                            </span>
                          </div>

                          {/* Color Swatches Details */}
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "0.4rem",
                              fontSize: "0.65rem",
                            }}
                          >
                            {colorFields.map(({ key, label }) => (
                              <div
                                key={key}
                                style={{
                                  display: "flex",
                                  alignItems: "flex-start",
                                  gap: "0.35rem",
                                }}
                              >
                                <div
                                  style={{
                                    width: "2rem",
                                    height: "2rem",
                                    backgroundColor:
                                      theme.colors[key] || "#d0d0d0",
                                    border:
                                      theme.colors[key] ||
                                      "1px solid var(--body-color)",
                                    borderRadius: "0.3rem",
                                  }}
                                />
                                <div>
                                  <p
                                    style={{
                                      color: "var(--text-color)",
                                      fontWeight: "600",
                                      fontSize: "0.65rem",
                                      margin: "0",
                                      lineHeight: "1",
                                    }}
                                  >
                                    {label}
                                  </p>
                                  <p
                                    style={{
                                      color: "var(--text-color)",
                                      opacity: 0.65,
                                      margin: "0",
                                      fontSize: "0.6rem",
                                      fontFamily: "monospace",
                                      lineHeight: "1",
                                    }}
                                  >
                                    {theme.colors[key] || "Not set"}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </motion.div>
                    ))}

                    {/* Add New Theme Button Card - Hidden when form is open */}
                    {editingIndex === null && !isAddingNew && (
                      <motion.button
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        onClick={handleAddNewClick}
                        className="glass__card"
                        style={{
                          border: "2px dashed var(--title-color)",
                          borderRadius: "0.5rem",
                          padding: "0.65rem",
                          cursor: "pointer",
                          backgroundColor:
                            "color-mix(in srgb, var(--title-color) 5%, var(--body-color))",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          minHeight: "140px",
                          transition: "all 0.3s ease",
                          color: "var(--title-color)",
                          fontSize: "2rem",
                          fontWeight: "700",
                        }}
                      >
                        <motion.span
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.95 }}
                          style={{ fontSize: "2.5rem", marginBottom: "0.5rem" }}
                        >
                          +
                        </motion.span>
                        <span style={{ fontSize: "0.7rem", fontWeight: "600" }}>
                          Add Theme
                        </span>
                      </motion.button>
                    )}
                  </div>
                ) : (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="glass__card"
                    style={{
                      padding: "2rem",
                      textAlign: "center",
                      backgroundColor:
                        "color-mix(in srgb, var(--title-color) 5%, var(--body-color))",
                      borderRadius: "0.8rem",
                      border:
                        "2px dashed color-mix(in srgb, var(--title-color) 25%, transparent)",
                    }}
                  >
                    <p style={{ color: "var(--title-color)", opacity: 0.6 }}>
                      No themes yet. Create your first one!
                    </p>
                  </motion.div>
                )}
              </motion.div>

              {/* Right Side - Form Panel (Visible when editing/adding) */}
              <AnimatePresence mode="wait">
                {(editingIndex !== null || isAddingNew) && (
                  <motion.div
                    initial={{ width: 0, opacity: 0 }}
                    animate={{ width: "270px", opacity: 1 }}
                    exit={{ width: 0, opacity: 0 }}
                    transition={{ duration: 0.6, ease: "easeInOut" }}
                    style={{
                      overflow: "hidden",
                      flexShrink: 0,
                    }}
                  >
                    {" "}
                    {(editingIndex !== null || isAddingNew) && (
                      <h4
                        style={{
                          fontSize: "1rem",
                          fontWeight: "700",
                          marginBottom: "1.7rem",
                          color: "var(--title-color)",
                        }}
                      >
                        {isAddingNew ? "✨ Create New Theme" : "✏️ Edit Theme"}
                      </h4>
                    )}
                    <div
                      className="glass__card"
                      style={{
                        flex: 3,
                        border: "2px solid var(--title-color)",
                        borderRadius: "0.5rem",
                        backgroundColor:
                          "color-mix(in srgb, var(--title-color) 5%, var(--body-color))",
                        overflow: "auto",
                        padding: "1rem",
                        height: "fit-content",
                      }}
                    >
                      {/* Theme Name Input */}
                      <div
                        style={{ position: "relative", marginBottom: "0.8rem" }}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                      >
                        <label
                          style={{
                            display: "block",
                            marginBottom: "0.3rem",
                            fontWeight: "600",
                            fontSize: "0.75rem",
                            color: "var(--title-color)",
                          }}
                        >
                          Theme Name *
                        </label>
                        <input
                          type="text"
                          value={editForm.name}
                          onChange={(e) =>
                            setEditForm((prev) => ({
                              ...prev,
                              name: e.target.value,
                            }))
                          }
                          placeholder="e.g., Modern Blue..."
                          style={{
                            width: "100%",
                            height: "2rem",
                            padding: "0 0.75rem",
                            border: errors.name
                              ? "2px solid #ff5050"
                              : "2px solid var(--title-color)",
                            borderRadius: "0.4rem",
                            fontSize: "0.8rem",
                            backgroundColor: "var(--body-color)",
                            color: "var(--border-color)",
                            outline: "none",
                            boxSizing: "border-box",
                            transition: "all 0.3s ease",
                          }}
                        />
                        {errors.name && (
                          <span
                            style={{
                              color: "#ff5050",
                              fontSize: "0.7rem",
                              marginTop: "0.3rem",
                              display: "block",
                            }}
                          >
                            {errors.name}
                          </span>
                        )}
                      </div>

                      {/* Color Inputs */}
                      {colorFields.map(({ key, label, icon }, idx) => (
                        <motion.div
                          key={key}
                          style={{
                            position: "relative",
                            marginBottom: "0.8rem",
                          }}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.1 * (idx + 2) }}
                        >
                          <label
                            style={{
                              marginBottom: "0.3rem",
                              fontWeight: "600",
                              fontSize: "0.7rem",
                              color: "var(--title-color)",
                              display: "flex",
                              alignItems: "center",
                              gap: "0.3rem",
                            }}
                          >
                            <i className={icon}></i> {label}
                          </label>
                          <div
                            style={{
                              display: "flex",
                              gap: "0.5rem",
                              alignItems: "center",
                            }}
                          >
                            <input
                              type="color"
                              value={editForm.colors[key] || "#ffffff"}
                              onChange={(e) =>
                                handleColorChange(key, e.target.value)
                              }
                              style={{
                                width: "45px",
                                height: "38px",
                                border: "2px solid var(--title-color)",
                                borderRadius: "0.4rem",
                                cursor: "pointer",
                                padding: "2px",
                              }}
                            />
                            <input
                              type="text"
                              value={editForm.colors[key] || ""}
                              onChange={(e) =>
                                handleColorChange(key, e.target.value)
                              }
                              placeholder="#000000"
                              style={{
                                flex: 1,
                                padding: "0.6rem 0.75rem",
                                border: "2px solid var(--title-color)",
                                borderRadius: "0.4rem",
                                backgroundColor: "var(--body-color)",
                                color: "var(--border-color)",
                                fontSize: "0.75rem",
                                boxSizing: "border-box",
                                fontFamily: "monospace",
                              }}
                            />
                          </div>
                        </motion.div>
                      ))}

                      {/* Form Buttons */}
                      <div
                        style={{
                          display: "flex",
                          gap: "0.5rem",
                          marginTop: "1rem",
                        }}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5 }}
                      >
                        <ButtonComponent
                          buttonText="Save"
                          onClick={handleSaveEdit}
                          className="btn-primary"
                        />
                        <ButtonComponent
                          buttonText="Cancel"
                          onClick={() => {
                            setEditingIndex(null);
                            setIsAddingNew(false);
                            setErrors({});
                          }}
                          className="btn-secondary"
                        />
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
};

export default EditDefaultThemesModal;
